// src/hooks/useEventCeremony.js
// ============================================================================
// The ceremony DIRECTOR — one hook that decides what a spectator sees between
// "registration closed" and "here are the Crits".
//
// Two data paths, on purpose:
//
//  1. onSnapshot(tournaments/{id})  — free, instant, public. It carries the
//     COUNT (playersReady) and the status flips, so the ticker jumps the moment
//     a racer readies. What it does NOT carry safely is names: the event doc's
//     playersReadyState holds `displayName`, which for a real account is the
//     person's legal name. Never render it.
//
//  2. getEventCeremony (callable) — the sanitized field: public handles, IGNs,
//     per-racer results as vision stamps them, Crits at payout. Polled, with
//     the snapshot used as an interrupt so a ready press refetches immediately
//     instead of waiting out the interval.
//
// Phase lives in constants/ceremony.js (computeCeremonyPhase) so the dev
// gallery can drive the same screens from mock state.
// ============================================================================

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

import { db } from '../../firebaseConfig';
import {
  CEREMONY_PHASE,
  POV_STATE,
  POLL_MS,
  HANDOFF_FADE_MS,
  GRID_LOCK_HOLD_MS,
  computeCeremonyPhase,
} from '../constants/ceremony';

const EMPTY_FIELD = [];

// Give up after this many consecutive endpoint failures; a tab regaining focus
// clears the count and tries again.
const MAX_CONSECUTIVE_FAILURES = 4;

/**
 * @param {string} eventId
 * @param {object} opts
 * @param {boolean} opts.hasLiveFeed   at least one stream resolves right now
 * @param {string}  opts.selectedUid   uid behind the camera currently on screen
 * @param {boolean} opts.enabled       master switch (screen mounted, not demo)
 * @param {object}  opts.mockState     dev gallery: use this instead of network
 */
export default function useEventCeremony(eventId, opts = {}) {
  const {
    hasLiveFeed = false,
    // Distinct from hasLiveFeed: a URL has resolved AND the player is painting.
    // The handoff dissolve waits on this one.
    feedReady = false,
    selectedUid = null,
    enabled = true,
    mockState = null,
  } = opts;

  const [state, setState] = useState(null);
  // Server clock minus local clock. Every countdown on screen is corrected by
  // this, so a spectator with a skewed machine still sees the real T-minus.
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  // Ticks once a second purely so countdowns and phase thresholds re-evaluate.
  const [, setTick] = useState(0);

  const phaseRef = useRef(CEREMONY_PHASE.OFF);
  const inFlightRef = useRef(false);
  const lastFetchRef = useRef(0);
  // Spectators sit on this screen for an hour. If the endpoint is unreachable
  // (not deployed yet, region misroute, invoker policy dropped after a deploy —
  // that last one has actually happened to getEventStreams), stop hammering it
  // and stop filling their console. A tab regaining focus rearms.
  const failuresRef = useRef(0);

  // --- 1. fetch the sanitized state ---------------------------------------
  const fetchState = useCallback(async (force = false) => {
    if (!eventId || !enabled || mockState) return;
    if (inFlightRef.current) return;
    if (failuresRef.current >= MAX_CONSECUTIVE_FAILURES) return;
    // Cheap guard against interrupt storms (a 16-racer field readying in a
    // burst writes the event doc 16 times).
    if (!force && Date.now() - lastFetchRef.current < 1200) return;

    inFlightRef.current = true;
    lastFetchRef.current = Date.now();
    try {
      const call = httpsCallable(getFunctions(), 'getEventCeremony');
      const res = await call({ eventId });
      const payload = res?.data;
      if (payload) {
        failuresRef.current = 0;
        if (payload.nowMs) setClockOffsetMs(payload.nowMs - Date.now());
        setState(payload);
      }
    } catch (err) {
      // Ceremony is decoration over a working watch page — a failure must never
      // take the stream down with it. Stay on the last good state.
      failuresRef.current += 1;
      if (typeof console !== 'undefined' && failuresRef.current <= MAX_CONSECUTIVE_FAILURES) {
        console.log(
          `[ceremony] state fetch failed (${failuresRef.current}/${MAX_CONSECUTIVE_FAILURES}):`,
          err?.message || err,
        );
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [eventId, enabled, mockState]);

  // --- 2. event-doc snapshot: instant interrupt ---------------------------
  useEffect(() => {
    if (!eventId || !enabled || mockState) return undefined;
    let lastSignal = '';
    const unsub = onSnapshot(
      doc(db, 'tournaments', eventId),
      (snap) => {
        if (!snap.exists()) return;
        const d = snap.data() || {};
        // Only the fields the ceremony reacts to — the doc is written on every
        // viewer-count and flood update too.
        const signal = [
          d.status,
          d.playersReady,
          d.readyStatus,
          d.championId,
          d.critPayoutsCompleted,
        ].join('|');
        if (signal !== lastSignal) {
          lastSignal = signal;
          fetchState(true);
        }
      },
      () => {},
    );
    return () => unsub();
  }, [eventId, enabled, mockState, fetchState]);

  // --- 3. poll at a phase-appropriate cadence -----------------------------
  useEffect(() => {
    if (!eventId || !enabled || mockState) return undefined;
    fetchState(true);
    let timer = null;
    const schedule = () => {
      const ms = POLL_MS[phaseRef.current] || 15000;
      timer = setTimeout(async () => {
        // A backgrounded tab shouldn't keep hitting the endpoint.
        if (typeof document === 'undefined' || document.visibilityState === 'visible') {
          await fetchState();
        }
        schedule();
      }, ms);
    };
    schedule();

    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        failuresRef.current = 0;  // rearm after a give-up
        fetchState(true);
      }
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (timer) clearTimeout(timer);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    };
  }, [eventId, enabled, mockState, fetchState]);

  // --- 4. 1s heartbeat so countdowns and thresholds move ------------------
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setTick((t) => (t + 1) % 100000), 1000);
    return () => clearInterval(id);
  }, [enabled]);

  // --- 4b. admin exemption from the ship-dark flag ------------------------
  // Same posture as useTeamStreamsVisible: the feature ships behind
  // settings/ceremony.enabled, and admins see it in prod regardless so it can
  // be exercised against a real event before the flag flips.
  const [isAdminUser, setIsAdminUser] = useState(false);
  useEffect(() => {
    if (mockState) { setIsAdminUser(true); return undefined; }
    // Subscribe rather than read currentUser once: auth usually resolves AFTER
    // mount, and a one-shot read would leave an admin permanently outside the
    // exemption on a hard page load.
    let active = true;
    const unsub = onAuthStateChanged(getAuth(), (u) => {
      if (!u) { if (active) setIsAdminUser(false); return; }
      getDoc(doc(db, 'users', u.uid))
        .then((snap) => {
          if (active) setIsAdminUser(!!(snap.exists() && snap.data()?.role === 'admin'));
        })
        .catch(() => { if (active) setIsAdminUser(false); });
    });
    return () => { active = false; unsub(); };
  }, [mockState]);

  // --- 5. derive ----------------------------------------------------------
  const effective = mockState || state;
  const now = Date.now() + clockOffsetMs;

  const visible = !!mockState || isAdminUser || effective?.ceremonyEnabled === true;

  // ONCE THE B-ROLL HAS HANDED OVER, IT MUST NOT COME BACK.
  //
  // The live branch of computeCeremonyPhase gates on feedReady so it can't
  // uncover a black player — but feedReady tracks the player's CURRENT state,
  // so a mid-race buffer stall or a POV switch would drop it back to false and
  // slam the b-roll over a running race. Latch it: the question the phase
  // machine actually needs answered is "has a picture ever arrived for this
  // event", not "is one painting this instant".
  //
  // Reset synchronously on eventId change rather than in an effect, so the
  // first render of a new event can't inherit the previous one's latch.
  const handedOffRef = useRef(false);
  const handoffEventRef = useRef(eventId);
  if (handoffEventRef.current !== eventId) {
    handoffEventRef.current = eventId;
    handedOffRef.current = false;
  }
  if (feedReady) handedOffRef.current = true;

  const phase = useMemo(() => {
    if (mockState?.phase) return mockState.phase;
    if (!enabled || !visible) return CEREMONY_PHASE.OFF;
    return computeCeremonyPhase(effective, now, hasLiveFeed, feedReady || handedOffRef.current);
    // `now` moves every tick; that's the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effective, enabled, visible, hasLiveFeed, feedReady, mockState, Math.floor(now / 1000)]);

  phaseRef.current = phase;

  // Remember when we entered GRID_LOCK / HANDOFF so those screens can run
  // their one-shot animation off a stable t0 instead of restarting each poll.
  const phaseEnteredAtRef = useRef({ phase: null, at: Date.now() });
  if (phaseEnteredAtRef.current.phase !== phase) {
    phaseEnteredAtRef.current = { phase, at: Date.now() };
  }
  // The gallery can pin elapsed time so a headless capture gets the same frame
  // every run instead of racing the animation.
  const phaseElapsedMs = mockState?.phaseElapsedMs != null
    ? mockState.phaseElapsedMs
    : Date.now() - phaseEnteredAtRef.current.at;

  const field = effective?.field || EMPTY_FIELD;

  // Per-POV state for whichever camera is on screen (founder step 5).
  // Gated on `visible` too: this drives a full-player overlay, so if it were
  // only gated at the phase level the POV card would still take the picture
  // while the whole feature is supposed to be dark.
  const povState = useMemo(() => {
    if (!selectedUid || !visible) return null;
    const row = field.find((f) => f.uid === selectedUid);
    if (!row) return null;
    return {
      uid: row.uid,
      name: row.name,
      ign: row.ign,
      state: row.state || POV_STATE.RACING,
      finalRank: row.finalRank,
      crits: row.crits,
    };
  }, [field, selectedUid, visible]);

  // Standings for the results ceremony: ranked finishers first, then everyone
  // who didn't get a rank (DNF last).
  const standings = useMemo(() => {
    const ranked = field.filter((f) => f.finalRank).sort((a, b) => a.finalRank - b.finalRank);
    const dnf = field.filter((f) => !f.finalRank && f.state === POV_STATE.DNF);
    const rest = field.filter((f) => !f.finalRank && f.state !== POV_STATE.DNF);
    return [...ranked, ...rest, ...dnf];
  }, [field]);

  // When the dissolve is allowed to start. The b-roll HOLDS until a picture is
  // actually up, so the spectator never sees a gap between the two — the whole
  // point of the sequence being one unbroken page.
  const feedReadyAtRef = useRef(null);
  if (feedReady && feedReadyAtRef.current == null) feedReadyAtRef.current = Date.now();
  if (!feedReady && feedReadyAtRef.current != null) feedReadyAtRef.current = null;
  const dissolveElapsedMs = feedReadyAtRef.current != null
    ? Date.now() - feedReadyAtRef.current
    : 0;

  return {
    phase,
    phaseElapsedMs,
    feedReady,
    dissolveElapsedMs,
    // true while the crossfade to the feeds is mid-flight
    isHandingOff: phase === CEREMONY_PHASE.HANDOFF && phaseElapsedMs < HANDOFF_FADE_MS,
    gridLockDone: phase === CEREMONY_PHASE.GRID_LOCK && phaseElapsedMs > GRID_LOCK_HOLD_MS,

    state: effective,
    field,
    standings,
    povState,

    readyCount: effective?.readyCount || 0,
    totalRequired: effective?.totalRequired || field.length || 0,
    // The countdown beat can start before registration actually closes (it
    // closes at startAt-15min; the ceremony opens a countdown up to 30 min
    // out), so the surface must not claim a lock that hasn't happened.
    registrationClosed: !!(
      effective?.lockedAtMs
      || ['locked', 'seeded'].includes(effective?.status)
      || (effective?.registrationClosesAtMs && effective.registrationClosesAtMs <= now)
    ),
    startsAtMs: effective?.startsAtMs || null,
    msUntilStart: effective?.startsAtMs ? effective.startsAtMs - now : null,
    potCrits: effective?.potCrits ?? null,
    netPoolCrits: effective?.netPoolCrits ?? null,
    entryCrits: effective?.entryCrits ?? null,
    // Crowd boost: running total is pre-split (live off the event doc);
    // critsEach is null until the distribution latch doc exists AND pays > 0.
    rumbleTotalCrits: effective?.rumbleTotalCrits ?? null,
    rumbleCritsEach: effective?.rumbleCritsEach ?? null,
    // How many times the crowd boosted (evergreen 3.3: floodCount +
    // rumbleCount). A count, never a figure: Rumbles are cash, so the
    // ceremony only says the crowd boosted the race. Null on an older server.
    rumbleCount: effective?.rumbleCount ?? null,
    // How many placements pay, per the bands the system already tapered to the
    // real field. Null when nothing has been stamped yet.
    payingPositions: effective?.payingPositions ?? null,
    title: effective?.title || null,
    championName: effective?.championName || null,
    resultsFinal: !!effective?.resultsFinal,
    ceremonyEnabled: !!effective?.ceremonyEnabled,

    refresh: () => fetchState(true),
  };
}
